import {
  Action,
  createAction,
  createAsyncThunk,
  createListenerMiddleware,
  createSlice,
  current,
} from '@reduxjs/toolkit';
import isEqual from 'lodash/isEqual';

import {
  AddressApi,
  AddressData,
  AdminAddressApi,
  EntityCustomShippingFeesApi,
  EntityDistanceShippingFeesApi,
  EntityLocationShippingFeesApi,
  FindEntityCustomShippingFeeToCreateBillQuery,
  FindEntityDistanceShippingFeeToCreateBillQuery,
  FindEntityShippingPolicyItemToCreateBillQuery,
  OrderApi,
  OrderData,
  ProductApi,
} from 'api';
import { RootState } from 'app';
import { PROMOTION_TYPE } from 'containers/SaleManager/Discount/Discount/commons';
import { formatNewSelectEntityProducts } from 'containers/WarehouseOperation/OrderOperation/commons';
import {
  ADJUSTMENT_TYPE,
  DELIVERY_FEE_TYPE,
  ORDER_DELIVERY_METHOD,
  ORDER_PAYMENT_METHOD,
  PartnerInfoType,
  SummaryOrderType,
  SupplierInfoType,
  ValidateConstraintsType,
} from 'utils';

import { ADDRESS_TYPE } from './addressSlice';

export const createOrderSliceListenerMiddleware = createListenerMiddleware();

export const takeLastedCalcWholesaleProductItemWhenCreateOrder = createAction<
  any | undefined
>('takeLastedCalcWholesaleProductItemWhenCreateOrder');

createOrderSliceListenerMiddleware.startListening({
  actionCreator: takeLastedCalcWholesaleProductItemWhenCreateOrder,
  effect: async (action: Action, listenerApi: any) => {
    // cancel other running instances
    listenerApi.cancelActiveListeners();
    await listenerApi.delay(800); // delay to wait next action before dispatching final one

    const { payload } = action as any;
    listenerApi.dispatch(
      checkOrderItemQuantityToCreateOrUpdateOrder({
        body: payload,
        isUpdatingProductItem: true,
      })
    );
  },
});

export const checkOrderItemQuantityToCreateOrUpdateOrder = createAsyncThunk(
  'createOrder/checkOrderItemQuantityToCreateOrUpdateOrder',
  async (
    params: { body: any; isUpdatingProductItem?: boolean },
    { rejectWithValue }
  ) => {
    const { body } = params;
    try {
      const response =
        await OrderApi.checkOrderItemQuantityToCreateOrUpdateOrder(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createWholesaleDraftOrderForPartner = createAsyncThunk(
  'createOrder/createWholesaleDraftOrderForPartner',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await OrderApi.createWholesaleDraftOrderForPartner(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createWholesaleOrderForPartner = createAsyncThunk(
  'createOrder/createWholesaleOrderForPartner',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await OrderApi.createWholesaleOrderForPartner(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createAddressForPartner = createAsyncThunk(
  'createOrder/createAddressForPartner',
  async (
    param: {
      id: string;
      body: AddressData;
    },
    { rejectWithValue }
  ) => {
    try {
      const { id, body } = param;
      const response = await AdminAddressApi.create(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const removeAddressForPartner = createAsyncThunk<
  any,
  string,
  { rejectValue: { message: string } }
>('createOrder/removeAddressForPartner', async (id, { rejectWithValue }) => {
  try {
    const response = await AdminAddressApi.delete(id);
    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const updateAddressForPartner = createAsyncThunk<
  any,
  { id: string; body: AddressData },
  { rejectValue: { message: string } }
>(
  'createOrder/updateAddressForPartner',
  async ({ id, body }, { rejectWithValue }) => {
    try {
      const response = await AdminAddressApi.update(id, body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findListAddressForPartner = createAsyncThunk(
  'createOrder/getListAddressForPartner',
  async (param: { id: string; query: any }, { rejectWithValue }) => {
    try {
      const { id, query } = param;
      const response = await AdminAddressApi.filter(id, query);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProductItemForCreateOrder = createAsyncThunk<
  any,
  { body: any },
  { rejectValue: { message: string } }
>(
  'updateOrder/findProductItemOrProductForCreate',
  async ({ body }, { rejectWithValue }) => {
    try {
      const response = await ProductApi.findProductToCreateOrder(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityCustomShippingFeeToCreateOrder = createAsyncThunk(
  'entityCustomShippingFee/findEntityCustomShippingFeeToCreateOrder',
  async (
    option: FindEntityCustomShippingFeeToCreateBillQuery,
    { rejectWithValue }
  ) => {
    try {
      const response = await EntityCustomShippingFeesApi.findToCreateBill(
        option
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityDistanceShippingFeeToCreateOrder = createAsyncThunk(
  'entityDistanceShippingFee/findEntityDistanceShippingFeeToCreateOrder',
  async (
    option: FindEntityDistanceShippingFeeToCreateBillQuery,
    { rejectWithValue }
  ) => {
    try {
      const response = await EntityDistanceShippingFeesApi.findToCreateBill(
        option
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityShippingPolicyItemToCreateOrder = createAsyncThunk(
  'entityShippingPolicyItem/findEntityShippingPolicyItemToCreateOrder',
  async (
    option: FindEntityShippingPolicyItemToCreateBillQuery,
    { rejectWithValue }
  ) => {
    try {
      const response = await EntityLocationShippingFeesApi.findToCreateBill(
        option
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

const updateStore = (
  state: any,
  data: any,
  shouldUpdateProducts: boolean
): void => {
  const {
    selectedProducts,
    summaryOrder,
    validationResult,
    totalCustomerQuantity,
  } = data;
  if (shouldUpdateProducts) {
    const currentSelectedProductItems = [...current(state).selectedProducts];
    const newSelectedProductItems = formatNewSelectEntityProducts({
      currentSelectedProductItems,
      selectedProducts,
    });
    state.selectedProducts = [...newSelectedProductItems];
  }

  state.deliveryFee = summaryOrder?.deliveryFee ?? 0;
  state.deliveryFeeType =
    summaryOrder?.deliveryFeeType ?? DELIVERY_FEE_TYPE.DEFAULT;
  state.summaryOrder = summaryOrder;
  state.promotions = (summaryOrder?.adjustments || [])
    .filter(
      (item: any) => item.type == ADJUSTMENT_TYPE.PROMOTION && item.price > 0
    )
    .map((item: any) => item.info);
  state.validationResult = validationResult;
  state.totalCustomerQuantity = totalCustomerQuantity;

  const findOrderSalePromotionCondition =
    summaryOrder.orderSalePromotionConditions.find(
      (item: any) => item.isSelected
    );

  state.selectedOrderSalePromotionCondition = findOrderSalePromotionCondition;

  state.customShippingFees = (state.customShippingFees || []).map(item => {
    const foundItem = (
      summaryOrder?.deliveryFeeDetail?.customShippingFees || []
    ).find(item1 => item1.id == item.id);
    if (foundItem) {
      return {
        ...item,
        ...foundItem,
        isSelected: true,
      };
    }
    return { ...item, isSelected: false };
  });
  state.shippingPolicyItems = (state.shippingPolicyItems || []).map(item => {
    if (
      summaryOrder?.deliveryFeeDetail?.entityShippingPolicyItem?.id == item.id
    ) {
      return {
        ...item,
        ...summaryOrder?.deliveryFeeDetail?.entityShippingPolicyItem,
        isSelected: true,
      };
    }
    return { ...item, isSelected: false };
  });
  state.distanceShippingFees = (state.distanceShippingFees || []).map(item => {
    if (
      summaryOrder?.deliveryFeeDetail?.entityDistanceShippingFee?.id == item.id
    ) {
      return {
        ...item,
        ...summaryOrder?.deliveryFeeDetail?.entityDistanceShippingFee,
        isSelected: true,
      };
    }
    return { ...item, isSelected: false };
  });
  state.locationShippingFee =
    summaryOrder?.deliveryFeeDetail?.entityShippingPolicyItem;
  state.distanceShippingFee =
    summaryOrder?.deliveryFeeDetail?.entityDistanceShippingFee;
};

const updateProductItemsWithValidationResult = (state: any): void => {
  const {
    isProposalItemDeleted,
    deletedProposalItemIDs,
    isProductDeleted,
    deletedProductIDs,
    isNotEnoughQuantity,
    notEnoughQuantityProducts,
    isPriceChanged,
    priceChangedProducts,
    isAddressChanged,
    changedAddress,
    isOrderEntityShippingPolicyItemChanged,
    changedOrderEntityShippingPolicyItem,
    isOrderEntityDistanceShippingFeeChanged,
    changedOrderEntityDistanceShippingFee,
    isOrderEntityCustomShippingFeesChanged,
    changedOrderEntityCustomShippingFees,
  } = state.validationResult;
  if (isAddressChanged && changedAddress) {
    let isSelectedAddress = false;
    const foundAddressIndex = state.addressData.findIndex(
      (item: any) => item.id === changedAddress.id
    );

    const changedAddressData = {
      ...changedAddress,
      ward: {
        id: changedAddress.ward?.id,
        code: changedAddress.ward?.code,
        fullName: changedAddress.ward?.fullName,
        district: changedAddress.district,
        province: changedAddress.province,
      },
      district: changedAddress.district,
      province: changedAddress.province,
      isSelected: isSelectedAddress,
    };
    if (foundAddressIndex !== -1) {
      isSelectedAddress = state.addressData[foundAddressIndex].isSelected;
      state.addressData[foundAddressIndex] = changedAddressData;
    }
    state.address = changedAddressData;
  }

  if (isProductDeleted) {
    state.selectedProducts = state.selectedProducts.filter(
      (item: any) => !deletedProductIDs.includes(item.productID)
    );
  }

  if (isNotEnoughQuantity) {
    state.selectedProducts.forEach(item => {
      const notEnoughQuantityProductItem = notEnoughQuantityProducts.find(
        (notEnoughQuantityItem: any) =>
          notEnoughQuantityItem.id === item.productID
      );

      if (notEnoughQuantityProductItem) {
        item.customerQuantity =
          notEnoughQuantityProductItem.newCustomerQuantity;
      }
    });
  }

  if (isPriceChanged) {
    state.selectedProducts.forEach(item => {
      const priceChangedProduct = priceChangedProducts.find(
        (priceChangedItem: any) => priceChangedItem.id === item.productID
      );

      if (priceChangedProduct) {
        if (
          priceChangedProduct.areAllPriceUnitsDeleted ||
          priceChangedProduct.isOldPriceUnitDeleted ||
          priceChangedProduct.newPriceUnitList?.length
        ) {
          item.priceUnit = priceChangedProduct.newPriceUnitList.filter(
            priceUnit => priceUnit.isSelected
          );
        } else {
          const indexPriceUnit = item.priceUnit.findIndex(
            priceUnit => priceUnit.id == priceChangedProduct.newPriceUnit.id
          );
          if (indexPriceUnit == -1) {
            item.priceUnit.push(priceChangedProduct.newPriceUnit);
          } else {
            item.priceUnit.splice(
              indexPriceUnit,
              1,
              priceChangedProduct.newPriceUnit
            );
          }
        }

        item.selectedPriceUnit = priceChangedProduct.newPriceUnit;
        item.customerQuantity = priceChangedProduct.newCustomerQuantity;
        item.oldPriceUnit = priceChangedProduct.newPriceUnit;
        if (priceChangedProduct.currentPrice) {
          item.originPrice = priceChangedProduct.currentPrice;
        }
        if (priceChangedProduct.originPrice) {
          item.originPrice = priceChangedProduct.originPrice;
        }
        item.productPricePolicyID = priceChangedProduct.productPricePolicyID;
        item.proposalItemID = priceChangedProduct.proposalItemID;
      }
    });
  }

  if (isOrderEntityShippingPolicyItemChanged) {
    state.shippingPolicyItems =
      changedOrderEntityShippingPolicyItem.entityShippingPolicyItems;
    state.locationShippingFee =
      changedOrderEntityShippingPolicyItem.newOrderEntityShippingPolicyItem;
    state.shippingPolicyItems?.forEach(item => {
      item.isSelected = state.locationShippingFee.id == item.id;
    });
  }

  if (isOrderEntityDistanceShippingFeeChanged) {
    state.distanceShippingFees =
      changedOrderEntityDistanceShippingFee.entityDistanceShippingFees;
    state.distanceShippingFee =
      changedOrderEntityDistanceShippingFee.newOrderEntityDistanceShippingFee;
    state.distanceShippingFees?.forEach(item => {
      item.isSelected = state.distanceShippingFee.id == item.id;
    });
  }

  if (isOrderEntityCustomShippingFeesChanged) {
    state.customShippingFees =
      changedOrderEntityCustomShippingFees.entityCustomShippingFees;
  }
};

const handleResetOrderDataUpdateSupplier = (state: any): void => {
  state.selectedProducts = [];
  state.deliveryMethod = ORDER_DELIVERY_METHOD.PLATFORM_DELIVERY;
  state.paymentMethod = ORDER_PAYMENT_METHOD.CASH;
  state.summaryOrder = initialSummaryOrder;
  state.totalCustomerQuantity = 0;
  state.validationResult = {};
  state.prevCustomerNote = '';
  state.customerNote = '';
  state.supplierNote = '';
  state.bankAccount = undefined;
  state.bankAccountID = undefined;
  state.images = [];
  state.deliveryNote = undefined;
  state.customerInfo = {};
  state.deliveryNote = undefined;
  state.address = undefined;
  state.addressData = [];
  state.oldEntityPricePolicyID = undefined;
  state.oldEntityProposalPolicyID = undefined;
  state.entityPricePolicyID = undefined;
  state.entityProposalPolicyID = undefined;

  state.customShippingFees = [];
  state.shippingPolicyItems = [];
  state.distanceShippingFees = [];
  state.deliveryFee = 0;
  state.deliveryFeeType = 0;
  state.locationShippingFee = undefined;
  state.distanceShippingFee = undefined;
};

const handleResetOrderDataUpdateCustomer = (state: any): void => {
  state.deliveryNote = undefined;
  state.address = undefined;
  state.addressData = [];
  state.deliveryFee = 0;
  state.deliveryFeeType = 0;
  state.locationShippingFee = undefined;
  state.distanceShippingFee = undefined;
};

const handleResetOrderData = (state: any): void => {
  state.selectedProducts = [];
  state.deliveryMethod = ORDER_DELIVERY_METHOD.PLATFORM_DELIVERY;
  state.paymentMethod = ORDER_PAYMENT_METHOD.CASH;
  state.summaryOrder = initialSummaryOrder;
  state.totalCustomerQuantity = 0;
  state.validationResult = {};
  state.prevCustomerNote = '';
  state.customerNote = '';
  state.supplierNote = '';
  state.bankAccount = undefined;
  state.images = [];
  state.deliveryNote = undefined;
  state.promotions = [];

  state.oldEntityPricePolicyID = undefined;
  state.entityPricePolicyID = undefined;
  state.entityProposalPolicyID = undefined;
  state.deliveryFee = 0;
  state.deliveryFeeType = 0;
  state.isCheckingOrderItemQuantity = false;

  state.locationShippingFee = undefined;
  state.distanceShippingFee = undefined;
  state.shippingPolicyItems = (state.shippingPolicyItems || []).map(item => {
    item.isSelected = false;
    return item;
  });
  state.distanceShippingFees = (state.distanceShippingFees || []).map(item => {
    item.isSelected = false;
    return item;
  });
  state.customShippingFees = (state.customShippingFees || []).map(item => {
    item.isSelected = false;
    return item;
  });
  state.isLoadingShippingPolicyItems = false;
  state.isLoadingDistanceShippingFees = false;
  state.isLoadingCustomShippingFees = false;
};

export interface CreateOrderInterface {
  shouldReloadCreateOrderPage: boolean;
  shouldUpdateCreateOrderData: boolean;
  selectedProducts: any[];
  productSearchResults: any[];

  chainSuppliersIDs: string[];
  supplierInfo?: SupplierInfoType;
  customerInfo?: any;

  deliveryMethod: ORDER_DELIVERY_METHOD;
  paymentMethod: ORDER_PAYMENT_METHOD;
  address: AddressData | undefined;
  addressData: AddressData[]; // List of partner addresses
  bankAccount: any | undefined;
  deliveryNote: any | undefined;
  images: any[];

  currentPage: number;
  totalPartnerDraftOrders: number;
  partnerDraftOrders: OrderData[];
  summaryOrder: SummaryOrderType;
  validationResult: ValidateConstraintsType;
  totalCustomerQuantity: number;
  promotions?: any[];
  selectedOrderSalePromotionCondition?: any;

  prevCustomerNote: string;
  customerNote: string;
  supplierNote: string;

  discount: number;
  fee: number;
  deliveryFee: number;
  deliveryFeeType: number;

  choiceProductItemList: any[];
  errorsCalcWholesaleProductItemForPartnerAfterUpdateQuantity: string[];

  oldEntityPricePolicyID?: string;
  oldEntityProposalPolicyID?: string;
  entityPricePolicyID?: string;
  entityProposalPolicyID?: string;

  isCheckingOrderItemQuantity: boolean;

  // phí giao hàng
  isLoadingCustomShippingFees: boolean;
  customShippingFees?: any[];
  isLoadingDistanceShippingFees: boolean;
  distanceShippingFees?: any[];
  isLoadingShippingPolicyItems: boolean;
  shippingPolicyItems?: any[];
  locationShippingFee: any | undefined;
  distanceShippingFee: any | undefined;
}

const validationResultInitialState: ValidateConstraintsType = {
  isValid: true,
  // deleted proposal items
  isProductItemDeleted: false,
  deletedProductItemIDs: [],
  // out of stock/ not enough proposal items
  isProductItemNotEnoughQuantity: false,
  notEnoughQuantityProductItems: [],
  notEnoughQuantityOrderItems: [],
  deactivatedProductIDs: [],
  isProductDeactivated: false,
  // price-changed proposal items
  isProductItemPriceChanged: false,
  priceChangedProductItems: [],
  priceChangedOrderItems: [],
  notEnoughQuantityProducts: [],
  isProductPriceChanged: false,
  priceChangedProducts: [],
  isAddressChanged: false,
  changedAddress: undefined,
};

const initialSummaryOrder: SummaryOrderType = {
  deliveryFeeType: 0,
  deliveryFee: 0,
  totalPrice: BigInt(0),
  totalTemporaryPrice: BigInt(0),
  totalItem: 0,
  totalAdjustment: BigInt(0),
  adjustments: [],
  orderSalePromotionConditions: [],
};

const initPartnerInfo: PartnerInfoType = {
  customerID: '',
  customerOwnerID: '',
  chainCustomerID: '',
  partnerID: '',
  avatar: '',
  displayName: '',
  pricePolicyID: '',
  proposalPolicyID: '',
  note: [],
  phoneNumber: '',
  totalDebt: 0,
};

const initSupplierInfo: SupplierInfoType = {
  id: '',
  avatar: '',
  displayName: '',
};

const initialState: CreateOrderInterface = {
  shouldReloadCreateOrderPage: false,
  shouldUpdateCreateOrderData: false,
  productSearchResults: [],
  selectedProducts: [],

  supplierInfo: initSupplierInfo,
  chainSuppliersIDs: [],
  customerInfo: {},

  deliveryMethod: ORDER_DELIVERY_METHOD.PLATFORM_DELIVERY,
  paymentMethod: ORDER_PAYMENT_METHOD.CASH,
  address: undefined,
  addressData: [],
  bankAccount: undefined,
  deliveryNote: undefined,
  images: [],

  currentPage: 0,
  totalPartnerDraftOrders: 0,
  partnerDraftOrders: [],

  summaryOrder: initialSummaryOrder,
  promotions: [],

  validationResult: validationResultInitialState,
  totalCustomerQuantity: 0,

  prevCustomerNote: '',
  customerNote: '',
  supplierNote: '',
  discount: 0,
  fee: 0,

  deliveryFee: 0,
  deliveryFeeType: 0,

  choiceProductItemList: [],
  errorsCalcWholesaleProductItemForPartnerAfterUpdateQuantity: [],

  oldEntityPricePolicyID: undefined,
  oldEntityProposalPolicyID: undefined,
  entityPricePolicyID: undefined,
  entityProposalPolicyID: undefined,

  isCheckingOrderItemQuantity: false,

  isLoadingCustomShippingFees: false,
  customShippingFees: [],
  isLoadingDistanceShippingFees: false,
  distanceShippingFees: [],
  isLoadingShippingPolicyItems: false,
  shippingPolicyItems: [],
  locationShippingFee: undefined,
  distanceShippingFee: undefined,
};

const createOrderSlice = createSlice({
  name: 'createOrder',
  initialState,
  reducers: {
    resetCreateOrderData(state) {
      state.shouldReloadCreateOrderPage = true;
      state.customerInfo = {};
      state.supplierInfo = initSupplierInfo;
      handleResetOrderDataUpdateCustomer(state);
      handleResetOrderData(state);
    },
    updatePartnerAddressData(state, { payload }) {
      state.addressData = payload;
    },
    updateSelectedPartnerAddress(state, { payload }) {
      state.address = payload;
    },
    updateCustomerNoteCreateOrder(state, { payload }) {
      state.customerNote = payload;
    },
    updateSupplierNote(state, { payload }) {
      state.supplierNote = payload;
    },
    updateSelectedProductsCreateOrder(state, { payload }) {
      const newSelectedProductItems = payload;
      state.selectedProducts = newSelectedProductItems;
      if (newSelectedProductItems.length === 0) {
        state.summaryOrder = initialSummaryOrder;
        state.selectedOrderSalePromotionCondition = undefined;
      }
    },
    updateSelectedProductCreateOrder(
      state,
      {
        payload,
      }: {
        payload: {
          id: string;
          quantity?: number;
          originPrice?: number;
        };
      }
    ) {
      const { id, quantity, originPrice } = payload;
      const selectedProduct = state.selectedProducts.find(
        item => item.id == id
      );
      if (selectedProduct && quantity != undefined) {
        selectedProduct.customerQuantity = Number(quantity);
      }
      if (selectedProduct && originPrice != undefined) {
        selectedProduct.originPrice = Number(originPrice);
      }
      if (selectedProduct?.promotions && selectedProduct?.promotions.length) {
        selectedProduct.promotions.forEach(promotion => {
          if (promotion.type === PROMOTION_TYPE.RATE && promotion.rate > 0) {
            promotion.adjustment = Math.round(
              (selectedProduct.originPrice *
                selectedProduct.customerQuantity *
                promotion.rate) /
                100
            );
          } else if (
            promotion.type === PROMOTION_TYPE.AMOUNT &&
            promotion.amount > 0
          ) {
            const amount =
              promotion.amount >= selectedProduct.originPrice
                ? selectedProduct.originPrice
                : promotion.amount;

            promotion.adjustment = Math.round(
              amount * selectedProduct.customerQuantity
            );
          }
        });
      }
    },
    updateOrderInfo(state, { payload }) {
      const orderInfo = payload;

      Object.keys(orderInfo).forEach(key => {
        if (key === 'customerInfo') {
          handleResetOrderDataUpdateCustomer(state);
          state[key] = orderInfo[key];
          return;
        }

        if (key == 'entityPricePolicyID') {
          state.oldEntityPricePolicyID = orderInfo[key] || '';
        }

        if (key == 'entityProposalPolicyID') {
          state.oldEntityProposalPolicyID = state.entityProposalPolicyID;
        }

        if (orderInfo[key] != undefined) {
          state[key] = orderInfo[key];
        }
      });
    },
    updateOrderInfoToCreateOrder(state, { payload }) {
      const orderInfo = payload;

      Object.keys(orderInfo).forEach(key => {
        if (key === 'supplierInfo') {
          state.customerInfo = initPartnerInfo;
          handleResetOrderDataUpdateSupplier(state);
        }

        if (key == 'entityPricePolicyID') {
          state.oldEntityPricePolicyID = state.entityPricePolicyID;
        }

        if (key == 'entityProposalPolicyID') {
          state.oldEntityProposalPolicyID = state.entityProposalPolicyID;
        }

        if (orderInfo[key] != undefined) {
          state[key] = orderInfo[key];
        }
      });
    },
    updatePaymentMethod(state, { payload }) {
      state.paymentMethod = payload.paymentMethod;
    },
    updateBankAccountInfo(state, { payload }) {
      if (
        state.bankAccount &&
        payload.bankAccount &&
        payload.bankAccount.id == state.bankAccount.id
      ) {
        state.bankAccount = undefined;
      } else {
        state.bankAccount = payload.bankAccount;
      }
    },
    updateDeliveryNote(state, { payload }) {
      state.deliveryNote = payload.deliveryNote;
    },
    updateLocationShippingFee(state, { payload }) {
      if (
        state.locationShippingFee &&
        payload.locationShippingFee &&
        isEqual(state.locationShippingFee, payload.locationShippingFee)
      ) {
        state.locationShippingFee = undefined;
      } else {
        state.locationShippingFee = payload.locationShippingFee;
      }
    },
    updateImages(state, { payload }) {
      state.images = payload?.images ?? [];
    },
    updateShouldUpdateCreateOrderData(state, { payload }) {
      state.shouldUpdateCreateOrderData = payload;
    },
    resetValidationResultCreateOrder: state => {
      state.validationResult = validationResultInitialState;
    },
    setChoiceProductItemList(state, { payload }) {
      state.choiceProductItemList = payload;
    },
    resetErrorsCalcWholesaleProductItemForPartnerAfterUpdateQuantity(state) {
      state.errorsCalcWholesaleProductItemForPartnerAfterUpdateQuantity = [];
    },
  },
  extraReducers: builder => {
    builder.addCase(findListAddressForPartner.fulfilled, (state, action) => {
      const { data } = action.payload;
      state.addressData = data;
      const selectedAddress = data.find(item => item.isSelected);
      if (selectedAddress) {
        state.address = data.find(item => item.isSelected);
      } else {
        if (data.length > 0) {
          state.address = data[0];
        } else {
          state.address = undefined;
        }
      }
    });
    builder.addCase(createAddressForPartner.fulfilled, (state, action) => {
      const { data } = action.payload;

      const address = { ...data, isSupplierCreateForPartner: true };
      if (state.addressData.length === 0) {
        state.address = address;
      }
      state.addressData.push(address);
    });
    builder.addCase(updateAddressForPartner.fulfilled, (state, action) => {
      const { data } = action.payload;

      const foundAddressIndex = state.addressData.findIndex(
        item => item.id === data?.id
      );
      if (foundAddressIndex != -1) {
        state.addressData[foundAddressIndex] = {
          ...data,
          isSupplierCreateForPartner: true,
        };
      }
      if (state.address?.id === data?.id) {
        state.address = data;
      }
    });
    builder.addCase(removeAddressForPartner.fulfilled, (state, action) => {
      const { data } = action.payload;
      const foundAddressIndex = state.addressData.findIndex(
        item => item.id === data?.id
      );
      if (foundAddressIndex !== -1) {
        state.addressData.splice(foundAddressIndex, 1);
      }
    });
    builder.addCase(
      checkOrderItemQuantityToCreateOrUpdateOrder.pending,
      (state, action) => {
        state.isCheckingOrderItemQuantity = true;
      }
    );
    builder.addCase(
      checkOrderItemQuantityToCreateOrUpdateOrder.rejected,
      (state, action) => {
        state.isCheckingOrderItemQuantity = false;
      }
    );
    builder.addCase(
      checkOrderItemQuantityToCreateOrUpdateOrder.fulfilled,
      (state, action) => {
        const { data } = action.payload;
        const { isUpdatingProductItem, body } = action.meta.arg;

        state.shouldUpdateCreateOrderData = !isUpdatingProductItem;
        state.entityPricePolicyID = body.entityPricePolicyID;

        if (data.validationResult && !data.validationResult.isValid) {
          state.shouldUpdateCreateOrderData = true;
          state.validationResult = data.validationResult;
          updateStore(state, data, true);
          updateProductItemsWithValidationResult(state);
        } else {
          updateStore(state, data, !isUpdatingProductItem);
        }
        state.isCheckingOrderItemQuantity = false;

        let newSelectedProductItems = [...current(state).selectedProducts];

        newSelectedProductItems = [...newSelectedProductItems].map(
          (item: any) => {
            const foundSelectedProduct = (data?.selectedProducts).find(
              (selectedProduct: any) => selectedProduct.productID == item.id
            );

            if (foundSelectedProduct) {
              return {
                ...item,
                promotions: foundSelectedProduct.promotions,
                salePromotionConditions:
                  foundSelectedProduct.salePromotionConditions,
              };
            }

            return item;
          }
        );
        state.selectedProducts = [...newSelectedProductItems];
      }
    );
    builder.addCase(
      createWholesaleDraftOrderForPartner.fulfilled,
      (state, action) => {
        const { data } = action.payload;
        if (data.validationResult && !data.validationResult.isValid) {
          updateStore(state, data, true);
          updateProductItemsWithValidationResult(state);
          state.validationResult = data.validationResult;
        } else {
          handleResetOrderData(state);
          const { order } = data;
          state.partnerDraftOrders.unshift({ ...order, pageIndex: 1 });
        }
      }
    );
    builder.addCase(
      createWholesaleOrderForPartner.fulfilled,
      (state, action) => {
        const { data } = action.payload;
        if (data.validationResult && !data.validationResult.isValid) {
          updateStore(state, data, true);
          updateProductItemsWithValidationResult(state);
          state.shouldUpdateCreateOrderData = true;
          state.validationResult = data.validationResult;
        } else {
          handleResetOrderData(state);
        }
      }
    );
    builder.addCase(
      findEntityCustomShippingFeeToCreateOrder.pending,
      (state, response) => {
        state.isLoadingCustomShippingFees = true;
      }
    );
    builder.addCase(
      findEntityCustomShippingFeeToCreateOrder.rejected,
      (state, response) => {
        state.isLoadingCustomShippingFees = false;
      }
    );
    builder.addCase(
      findEntityCustomShippingFeeToCreateOrder.fulfilled,
      (state, action) => {
        const { data } = action.payload;
        state.customShippingFees = data?.map(item => {
          const foundItem = (
            state.summaryOrder?.deliveryFeeDetail?.customShippingFees || []
          ).find(item1 => item1.id == item.id);
          if (foundItem) {
            return {
              ...item,
              ...foundItem,
              isSelected: true,
            };
          }
          return { ...item, isSelected: false };
        });
        state.isLoadingCustomShippingFees = false;
      }
    );
    builder.addCase(
      findEntityDistanceShippingFeeToCreateOrder.pending,
      (state, response) => {
        state.isLoadingDistanceShippingFees = true;
      }
    );
    builder.addCase(
      findEntityDistanceShippingFeeToCreateOrder.rejected,
      (state, response) => {
        state.isLoadingDistanceShippingFees = false;
      }
    );
    builder.addCase(
      findEntityDistanceShippingFeeToCreateOrder.fulfilled,
      (state, action) => {
        const { data } = action.payload;
        state.distanceShippingFees = data?.map(item => {
          if (
            state.summaryOrder?.deliveryFeeDetail?.entityDistanceShippingFee
              ?.id == item.id
          ) {
            return {
              ...item,
              ...state.summaryOrder?.deliveryFeeDetail
                ?.entityDistanceShippingFee,
              isSelected: true,
            };
          }
          return { ...item, isSelected: false };
        });
        state.isLoadingDistanceShippingFees = false;
      }
    );
    builder.addCase(
      findEntityShippingPolicyItemToCreateOrder.pending,
      (state, response) => {
        state.isLoadingShippingPolicyItems = true;
      }
    );
    builder.addCase(
      findEntityShippingPolicyItemToCreateOrder.rejected,
      (state, response) => {
        state.isLoadingShippingPolicyItems = false;
      }
    );
    builder.addCase(
      findEntityShippingPolicyItemToCreateOrder.fulfilled,
      (state, action) => {
        const { data } = action.payload;
        state.shippingPolicyItems = data?.map(item => {
          if (
            state.summaryOrder?.deliveryFeeDetail?.entityShippingPolicyItem
              ?.id == item.id
          ) {
            return {
              ...item,
              ...state.summaryOrder?.deliveryFeeDetail
                ?.entityShippingPolicyItem,
              isSelected: true,
            };
          }
          return { ...item, isSelected: false };
        });
        state.isLoadingShippingPolicyItems = false;
      }
    );
  },
});

const { actions, reducer: createOrderReducer } = createOrderSlice;

export const {
  updateSelectedProductsCreateOrder,
  updateSelectedProductCreateOrder,
  updateOrderInfo,
  updateOrderInfoToCreateOrder,
  updateCustomerNoteCreateOrder,
  updateSupplierNote,
  updateShouldUpdateCreateOrderData,
  resetCreateOrderData,
  resetValidationResultCreateOrder,
  setChoiceProductItemList,
  resetErrorsCalcWholesaleProductItemForPartnerAfterUpdateQuantity,
  updatePaymentMethod,
  updateBankAccountInfo,
  updateDeliveryNote,
  updateLocationShippingFee,
  updateImages,
} = actions;

export const selectCreateOrder = (state: RootState): CreateOrderInterface =>
  state.createOrder as CreateOrderInterface;

export default createOrderReducer;
